import { parsePayload, type LinkPayload } from './payload'

/**
 * The transport that exists because the phone is the web server.
 *
 * When this page was served by the phone, the payload is one same-origin fetch away: no camera, no
 * WebRTC handshake, no signalling, and no third party. The browser is allowed to fetch it for the
 * single reason that makes this whole design work, which is that the page and the data now share
 * an origin.
 *
 * On covellah.com none of this applies. `/link/status` is not a route there, the probe fails fast,
 * and the portal falls back to the file door without the woman ever learning that a third
 * transport was considered.
 */

export interface LocalHostStatus {
  covellaLocal: true
  needsCode: boolean
}

/**
 * Is this page coming from a phone?
 *
 * Deliberately a question asked of the server rather than of the URL. Sniffing `location.port` or
 * a private-IP hostname guesses, and guesses wrongly on a laptop that happens to run the portal on
 * the same port in development.
 */
export async function probeLocalHost(signal?: AbortSignal): Promise<LocalHostStatus | null> {
  try {
    const response = await fetch('/link/status', { signal, cache: 'no-store' })
    if (!response.ok) return null
    const body = (await response.json()) as Partial<LocalHostStatus>
    return body?.covellaLocal === true ? { covellaLocal: true, needsCode: body.needsCode !== false } : null
  } catch {
    return null
  }
}

export class WrongCode extends Error {
  constructor(message = 'That code does not match the one on your phone.') {
    super(message)
  }
}

export class LockedOut extends Error {
  constructor() {
    super(
      'Too many wrong codes, so your phone closed the connection. Open Read on my laptop again to start over.',
    )
  }
}

/**
 * Trades the six digits on the phone's screen for the payload.
 *
 * The phone builds it only once the code is right, so a wrong guess costs an attacker a strike
 * and yields nothing at all, not even the size of what they were reaching for.
 */
export async function fetchLocalPayload(code: string): Promise<LinkPayload> {
  const response = await fetch('/link/payload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
    cache: 'no-store',
  })

  if (response.status === 401) throw new WrongCode()
  if (response.status === 429) throw new LockedOut()
  if (!response.ok) {
    throw new Error('Your phone could not build the export. Try again from the app.')
  }

  const envelope = (await response.json()) as { covella?: string; payload?: unknown }
  // The phone sends the same envelope shape it writes to a file, so one parser serves both doors.
  // It is never sealed over this transport: a passphrase would protect the payload from the
  // network it never travels on, at the cost of asking her to type one more thing.
  return parsePayload(envelope?.payload ?? envelope)
}
